import React from 'react'
import {
  Select as ChakraSelect,
  SelectProps as ChakraSelectProps,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
} from '@chakra-ui/react'
import {
  FieldError,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form'
import { SwashbuckleComponentProps } from '../../types'
import { Icon } from '../Icon/Icon'
import { useSelectProps } from '../../hooks/useSelectProps'

type OmittedProps =
  | 'colorScheme'
  | 'variant'
  | 'icon'
  | 'iconColor'
  | 'iconSize'
  | 'errorBorderColor'
  | 'focusBorderColor'
  | 'rootProps'

export type SelectProps<TFormValues extends FieldValues> = {
  label: string
  error?: FieldError
  helperText?: string
  register?: (name: string, RegisterOptions) => UseFormReturn<TFormValues>
  name: string
  rules?: RegisterOptions
} & Omit<SwashbuckleComponentProps<ChakraSelectProps>, OmittedProps>

export const Select = <TFormValues extends FieldValues>(
  props: SelectProps<TFormValues>
) => {
  const selectProps = useSelectProps(props)
  const {
    'data-qa': dataQa,
    className,
    error,
    helperText,
    isDisabled,
    isReadOnly,
    isRequired,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    register,
    rules,
    ...chakraSelectProps
  } = selectProps

  const isError = !!error

  const errorColor = 'text-danger'
  const focusColor = 'text-primary'

  return (
    <FormControl
      data-qa={dataQa}
      id={name}
      isInvalid={isError}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      className={className}
      // @ts-expect-error Need to dig into this, but running out of time
      onBlur={onBlur}
      // @ts-expect-error Need to dig into this, but running out of time
      onChange={onChange}
      // @ts-expect-error Need to dig into this, but running out of time
      onFocus={onFocus}
    >
      <FormLabel>{label}</FormLabel>
      <ChakraSelect
        focusBorderColor={focusColor}
        errorBorderColor={errorColor}
        icon={<Icon name='triangle-down' />}
        {...chakraSelectProps}
        {...register?.(name, rules)}
      />
      {isError ? (
        <FormErrorMessage color={errorColor}>{error?.message}</FormErrorMessage>
      ) : (
        // If no helper text, we still want to display this element
        // so that the space for the error message is accounted for and we avoid
        // the inputs shifting in a form when errors appear
        <FormHelperText>{helperText || '\u200B'}</FormHelperText>
      )}
    </FormControl>
  )
}
